// Slow Keys service — a key only counts as "typed" if the user holds
// it for the configured delay. Short taps are discarded. Helps users
// with motor-control issues avoid accidental keystrokes.
//
// While a key is being evaluated, a small progress ring is shown
// so the user can see how close they are to the acceptance threshold.

let started = false;
let store = null;
let pending = new Map(); // key -> { start, timeout }
let ring = null;

function ensureRing() {
  if (ring) return;
  ring = document.createElement('div');
  ring.setAttribute('aria-hidden', 'true');
  ring.style.cssText = [
    'position:fixed',
    'bottom:60px',
    'left:50%',
    'transform:translateX(-50%)',
    'background:#111827',
    'color:#ffffff',
    'padding:10px 16px',
    'border-radius:12px',
    'font-family:Poppins,sans-serif',
    'font-size:12px',
    'font-weight:600',
    'box-shadow:0 8px 20px rgba(0,0,0,0.25)',
    'z-index:9998',
    'display:none',
    'gap:10px',
    'align-items:center'
  ].join(';');
  ring.innerHTML = `
    <span class="a11y-slow-label">Hold key…</span>
    <svg width="20" height="20" viewBox="0 0 20 20" style="flex-shrink:0;">
      <circle cx="10" cy="10" r="8" fill="none" stroke="rgba(255,255,255,0.25)" stroke-width="2" />
      <circle class="a11y-slow-ring" cx="10" cy="10" r="8" fill="none"
              stroke="#f59e0b" stroke-width="2" stroke-linecap="round"
              stroke-dasharray="50.27" stroke-dashoffset="50.27"
              transform="rotate(-90 10 10)" />
    </svg>
  `;
  document.body.appendChild(ring);
}

function showRing(delay) {
  if (!ring) return;
  ring.style.display = 'inline-flex';
  const circle = ring.querySelector('.a11y-slow-ring');
  if (circle) {
    circle.style.transition = 'none';
    circle.style.strokeDashoffset = '50.27';
    // eslint-disable-next-line no-unused-expressions
    circle.getBoundingClientRect();
    circle.style.transition = `stroke-dashoffset ${delay}ms linear`;
    circle.style.strokeDashoffset = '0';
  }
}

function hideRing() {
  if (!ring) return;
  ring.style.display = 'none';
  const circle = ring.querySelector('.a11y-slow-ring');
  if (circle) {
    circle.style.transition = 'none';
    circle.style.strokeDashoffset = '50.27';
  }
}

function onKeyDown(e) {
  if (!store) return;
  // Allow sticky keys / modifiers to pass through
  if (
    ['Shift', 'Control', 'Alt', 'Meta', 'CapsLock', 'Tab', 'Escape'].includes(
      e.key
    )
  )
    return;
  if (e.repeat) return; // ignore auto-repeat

  const f = store.getters['Accessibility/getFeatures'];
  const delay = Math.max(100, Math.min(2000, f.slowKeysDelay || 400));

  // Capture the keydown until the user holds for `delay` ms
  if (pending.has(e.key)) return;

  e.preventDefault();
  e.stopImmediatePropagation();

  showRing(delay);

  const target = e.target || document.activeElement || document.body;
  const timeout = setTimeout(() => {
    pending.delete(e.key);
    hideRing();
    // Now dispatch the key — as a real input event on input fields,
    // or as a keydown for everything else.
    const tag = target && target.tagName;
    if (
      (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) &&
      e.key.length === 1
    ) {
      // Type the character directly
      if (target.isContentEditable) {
        target.focus();
        document.execCommand('insertText', false, e.key);
      } else {
        const start = target.selectionStart ?? target.value.length;
        const end = target.selectionEnd ?? target.value.length;
        target.value =
          target.value.slice(0, start) + e.key + target.value.slice(end);
        target.selectionStart = target.selectionEnd = start + 1;
        target.dispatchEvent(new Event('input', { bubbles: true }));
      }
    } else {
      // Re-dispatch keydown for non-input targets (so app shortcuts work)
      const init = {
        key: e.key,
        code: e.code,
        bubbles: true,
        cancelable: true,
        shiftKey: e.shiftKey,
        ctrlKey: e.ctrlKey,
        altKey: e.altKey,
        metaKey: e.metaKey
      };
      target.dispatchEvent(new KeyboardEvent('keydown', init));
      target.dispatchEvent(new KeyboardEvent('keyup', init));
    }
  }, delay);

  pending.set(e.key, { start: Date.now(), timeout, target });
}

function onKeyUp(e) {
  const p = pending.get(e.key);
  if (!p) return;
  clearTimeout(p.timeout);
  pending.delete(e.key);
  hideRing();
}

export function startSlowKeys(vuexStore) {
  if (started) return;
  store = vuexStore;
  ensureRing();
  document.addEventListener('keydown', onKeyDown, true);
  document.addEventListener('keyup', onKeyUp, true);
  started = true;
}

export function stopSlowKeys() {
  if (!started) return;
  document.removeEventListener('keydown', onKeyDown, true);
  document.removeEventListener('keyup', onKeyUp, true);
  pending.forEach(({ timeout }) => clearTimeout(timeout));
  pending.clear();
  hideRing();
  if (ring && ring.parentNode) ring.parentNode.removeChild(ring);
  ring = null;
  started = false;
}
